import { svg } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { KeyScale } from '../axis/key/KeyScale.js';
import { ValueScale } from '../axis/value/ValueScale.js';
import { ColumnOverlappedRenderGroup, columnOverlappedRenderGroupData } from './types.js';
import { ValueFormatter } from '../../../visualizations/common/format.js';
import { createInnerKeyScale, getX } from './calculations.js';
import { PointLabelCollisionManager } from '../point-label/PointLabelCollisionManager.js';
import { interpolate, svgTransition } from '../../../visualizations/common/directives/svgTransition.js';
import { timing } from '../animation.js';
import { OneUxPlotChartTypographyToken, highlightStateProvider } from '../types.js';
const typography: OneUxPlotChartTypographyToken = 'label-100';
export function ColumnOverlappedPointLabel({
  keyScale,
  valueScale,
  group,
  valueFormatter,
  pointLabelCollisionManager,
  animate,
  highlightStateProvider
}: {
  keyScale: KeyScale;
  valueScale: ValueScale;
  group: ColumnOverlappedRenderGroup;
  valueFormatter: ValueFormatter;
  pointLabelCollisionManager: PointLabelCollisionManager;
  animate: boolean;
  highlightStateProvider: highlightStateProvider;
}) {
  const innerKeyScale = createInnerKeyScale(keyScale, group);
  const data = group.data.filter(dp => {
    if (!canRenderPointLabel(dp, valueScale, highlightStateProvider)) {
      return false;
    }
    const {
      x,
      y,
      dx,
      dy,
      dominantBaseline,
      value
    } = getPointLabelPositionData(dp, keyScale, valueScale, innerKeyScale, valueFormatter, pointLabelCollisionManager);
    const isIntersectingOtherPointLabel = pointLabelCollisionManager.isCollision(value, x, y, dx, dy, 'middle', dominantBaseline, typography);
    return !isIntersectingOtherPointLabel;
  });
  return repeat(data, dp => `pointlabel:${dp.name}:${dp.keyIndex}:${dp.value}`, dp => {
    const {
      x,
      y,
      dx,
      dy,
      dominantBaseline,
      value
    } = getPointLabelPositionData(dp, keyScale, valueScale, innerKeyScale, valueFormatter, pointLabelCollisionManager);
    return svg`<text
        text-anchor="middle"
        dominant-baseline=${dominantBaseline}
        fill="var(--one-ux-palette--brand-neutral-600)"
        class="one-ux-typography--${typography}"
        style="margin: 0;"
        ${svgTransition({
      timing: timing(animate),
      attributes: {
        x: interpolate.constant(x, x => x.toString()),
        y: interpolate.constant(y, y => y.toString()),
        dx: interpolate.constant(dx, dx => dx.toString()),
        dy: interpolate.constant(dy, dy => dy.toString()),
        opacity: interpolate.standard(0, 1, 0)
      }
    })}>
          ${value}
        </text>`;
  });
}
const canRenderPointLabel = (dp: columnOverlappedRenderGroupData, valueScale: ValueScale, highlightStateProvider: highlightStateProvider) => {
  const isValueOutOfBounds = valueScale.getLimitPassed(dp.value) !== 'none';
  if (isValueOutOfBounds) return false;
  const highlightState = highlightStateProvider(dp.name);
  if (highlightState === 'active') return true;
  if (!dp.showPointLabel || highlightState === 'inactive') return false;
  return true;
};
const getPointLabelPositionData = (dp: columnOverlappedRenderGroupData, keyScale: KeyScale, valueScale: ValueScale, innerKeyScale: KeyScale, valueFormatter: ValueFormatter, pointLabelCollisionManager: PointLabelCollisionManager) => {
  const x = getX(innerKeyScale, dp);
  const y = valueScale.clampPosition(dp.value);
  const dx = innerKeyScale.size / 2;
  let dy = dp.value >= 0 ? -4 : 4;
  const dominantBaseline: 'auto' | 'hanging' = dp.value >= 0 ? 'auto' : 'hanging';
  const value = valueFormatter.formatValue(dp.value);
  const isKeyScaleIntersection = pointLabelCollisionManager.isIntersectingKeyScale(keyScale, value, x, y, dx, dy, 'middle', dominantBaseline);
  if (isKeyScaleIntersection) {
    dy -= 14;
  }
  return {
    value,
    x,
    y,
    dx,
    dy,
    dominantBaseline
  };
};